import { useState } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Plus, Check, Loader2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import type { EmojiPack } from '@/hooks/useEmojiPacks';
import { cn } from '@/lib/utils';

interface EmojiPackCardProps {
  pack: EmojiPack;
  isAdded?: boolean;
  onAdd?: (pack: EmojiPack) => Promise<void>;
  onRemove?: (pack: EmojiPack) => Promise<void>;
  showAuthor?: boolean;
}

export function EmojiPackCard({ pack, isAdded, onAdd, onRemove, showAuthor = true }: EmojiPackCardProps) {
  const [loading, setLoading] = useState(false);
  const author = useAuthor(pack.pubkey);
  const displayName = author.data?.metadata?.name ?? genUserName(pack.pubkey);
  const avatar = author.data?.metadata?.picture;
  const previewEmojis = pack.emojis.slice(0, 8);

  const naddr = nip19.naddrEncode({
    kind: 30030,
    pubkey: pack.pubkey,
    identifier: pack.dTag,
  });

  const handleToggle = async () => {
    setLoading(true);
    try {
      if (isAdded && onRemove) {
        await onRemove(pack);
      } else if (!isAdded && onAdd) {
        await onAdd(pack);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="group relative rounded-2xl border border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20 transition-all duration-300 overflow-hidden backdrop-blur-sm">
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-500/0 via-fuchsia-500/0 to-pink-500/0 group-hover:from-violet-500/5 group-hover:via-fuchsia-500/5 group-hover:to-pink-500/5 transition-all duration-500 pointer-events-none" />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <Link
              to={`/${naddr}`}
              className="block text-base font-semibold text-white/90 group-hover:text-white truncate transition-colors hover:text-violet-300"
            >
              {pack.title}
            </Link>
            {showAuthor && (
              <div className="flex items-center gap-1.5 mt-1">
                {avatar ? (
                  <img
                    src={avatar}
                    alt={displayName}
                    className="w-4 h-4 rounded-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <User className="w-3.5 h-3.5 text-white/40" />
                )}
                <span className="text-xs text-white/40 truncate max-w-[120px]">{displayName}</span>
              </div>
            )}
          </div>

          {(onAdd || onRemove) && (
            <Button
              size="sm"
              variant={isAdded ? 'secondary' : 'default'}
              onClick={handleToggle}
              disabled={loading}
              className={cn(
                'ml-3 h-8 px-3 rounded-full text-xs font-medium shrink-0 transition-all',
                isAdded
                  ? 'bg-white/10 text-white/70 hover:bg-red-500/20 hover:text-red-300 hover:border-red-500/30'
                  : 'bg-violet-500 hover:bg-violet-400 text-white shadow-lg shadow-violet-500/30',
              )}
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : isAdded ? (
                <><Check className="w-3.5 h-3.5 mr-1" />Added</>
              ) : (
                <><Plus className="w-3.5 h-3.5 mr-1" />Add</>
              )}
            </Button>
          )}
        </div>

        {/* Emoji preview grid */}
        <Link to={`/${naddr}`} className="block">
          <div className="grid grid-cols-4 gap-2 mb-4">
            {previewEmojis.map((emoji) => (
              <div
                key={emoji.shortcode}
                className="aspect-square rounded-xl bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center p-2 group/emoji"
                title={`:${emoji.shortcode}:`}
              >
                <img
                  src={emoji.url}
                  alt={emoji.shortcode}
                  className="w-full h-full object-contain group-hover/emoji:scale-110 transition-transform duration-200"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ))}
            {pack.emojis.length > 8 && (
              <div className="aspect-square rounded-xl bg-white/5 flex items-center justify-center">
                <span className="text-xs text-white/40 font-medium">+{pack.emojis.length - 8}</span>
              </div>
            )}
          </div>
        </Link>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <Badge variant="secondary" className="bg-white/10 text-white/50 border-0 text-xs">
            {pack.emojis.length} {pack.emojis.length === 1 ? 'emoji' : 'emojis'}
          </Badge>
          {pack.description && (
            <span className="text-xs text-white/30 truncate ml-2 max-w-[140px]">{pack.description}</span>
          )}
        </div>
      </div>
    </div>
  );
}
